import { stateOptions } from '../commonStateTaxData';

const basicInfoQuestions = [
  {
    id: 'employee_first_name',
    title: 'First name',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'employee_middle_name',
    title: 'Middle initial',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[a-zA-Z]{1}$',
        customErrorMessage: 'The middle initial can only contain 1 letter.',
      },
    ],
  },
  {
    id: 'employee_last_name',
    title: 'Last name',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'employee_ssn',
    title: 'Social Security number',
    is_required: true,
    placeholder: '9 digits',
    question_type: 'identity',
    identity_type: 'ssn',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^\\d{9}$',
      },
    ],
  },
  {
    id: 'street_address',
    title: 'Home address',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'city_or_town',
    title: 'City or town',
    is_required: true,
    question_type: 'short_text',
  },
  {
    id: 'state',
    title: 'State',
    is_required: true,
    question_type: 'dropdown',
    options: stateOptions,
  },
  {
    id: 'zip_code',
    title: 'Zip code',
    is_required: true,
    question_type: 'short_text',
    placeholder: '5 digits',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^\\d{5}$',
      },
    ],
  },
  {
    id: 'filing_status',
    title: 'Please select tax filing status:',
    is_required: true,
    question_type: 'multiple_choice',
    options: [
      {
        codeValue: 'single',
        shortName: 'Single',
      },
      {
        codeValue: 'married_filing_jointly',
        shortName: 'Married or domestic partners filing jointly',
      },
      {
        codeValue: 'married_filing_separately',
        shortName: 'Married filing separately',
      },
      {
        codeValue: 'head_of_household',
        shortName: 'Head of household',
      },
      {
        codeValue: 'married_filing_separately_on_same_return',
        shortName: 'Married or domestic partners filing separately on the same return',
      },
    ],
  },
];

export const getBasicInfoQuestions = (version) => {
  if (version === '12_13') {
    return basicInfoQuestions;
  }

  // Reference Form: https://check-api-static-production.s3.amazonaws.com/forms/pdf/dc_d4_01.pdf
  return basicInfoQuestions.map((question) => {
    if (question.id === 'employee_ssn') {
      return {
        ...question,
        title: 'Taxpayer Identification Number (TIN)',
      };
    }
    return question;
  });
};

const applicableAllowancesQuestion = [
  {
    id: 'applicable_allowances',
    title: 'Select any allowances that apply to you.',
    question_type: 'long_multiple_selection',
    is_required: false,
    options: [
      {
        codeValue: 'allowance_for_self',
        shortName: 'Allowance for yourself',
      },
      {
        codeValue: 'filing_as_head',
        shortName: 'If you are filing as head of household',
      },
      {
        codeValue: 'over_65',
        shortName: 'If you are 65 or over',
      },
      {
        codeValue: 'blind',
        shortName: 'If you are blind',
      },
      {
        codeValue: 'filing_jointly_spouse',
        shortName: 'For your spouse/registered partner if filing jointly',
      },
      {
        codeValue: 'filing_jointly_spouse_over_65',
        shortName:
          'If married/registered domestic partners filing jointly and your spouse/registered domestic partner is 65 or over',
      },
      {
        codeValue: 'filing_jointly_spouse_blind',
        shortName:
          'If married/registered domestic partners filing jointly and your spouse/registered domestic partner is blind',
      },
    ],
  },
];

const applicableAllowancesQuestion1017 = [
  {
    id: 'applicable_allowances',
    title: 'Select any allowances that apply to you.',
    question_type: 'long_multiple_selection',
    is_required: false,
    options: [
      {
        codeValue: 'allowance_for_self',
        shortName: 'Allowance for yourself (Line a)',
      },
      {
        codeValue: 'filing_as_head',
        shortName: 'If you are filing as head of household (Line b)',
      },
      {
        codeValue: 'over_65',
        shortName: 'If you are 65 or over (Line c)',
      },
      {
        codeValue: 'blind',
        shortName: 'If you are blind (Line d)',
      },
      {
        codeValue: 'filing_jointly_spouse',
        shortName: 'If your spouse or registered domestic partner filing jointly or filing separately on same return or if you are a qualifying widow(er) with dependent child (Line f)',
      },
      {
        codeValue: 'filing_jointly_spouse_over_65',
        shortName:
          'if married or registered domestic partner filing jointly or filing separately on same return and your spouse or registered domestic partner is 65 or over (Line g)',
      },
      {
        codeValue: 'filing_jointly_spouse_blind',
        shortName:
          'If married or registered domestic partner filing jointly or filing separately on same return and your spouse or registered domestic partner is blind (Line h)',
      },
    ],
  },
];

export const getApplicableAllowancesQuestion = (version) => {
  if (version === '12_13') return applicableAllowancesQuestion;

  return applicableAllowancesQuestion1017;
};

export const worksheetSectionAQuestions = [
  {
    id: 'dependents',
    title: 'Enter the number of dependents you want to claim (Line e)',
    question_type: 'short_text',
    is_required: true,
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[0-9]+(.[0-9]{1,2})?$',
      },
      {
        value: '100',
        action: 'lte',
        category: 'number',
      },
    ],
  },
];

const worksheetSectionBQuestions = [
  {
    id: 'itemized_deductions',
    title: 'Enter your estimate of your itemized deductions',
    question_type: 'short_text',
    is_required: true,
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[0-9]+(.[0-9]{1,2})?$',
      },
    ],
  },
  {
    id: 'filing_status_b',
    title: 'Select your filing status:',
    question_type: 'multiple_choice',
    is_required: true,
    options: [
      {
        codeValue: 'married_filing_separately',
        shortName: 'Married/registered domestic partners filing separately ($2,000 standard deduction)',
      },
      {
        codeValue: 'other',
        shortName: 'All others ($4,000 allowance)',
      },
    ],
  },
];

const worksheetSectionBQuestions1017 = [
  {
    id: 'itemized_deductions',
    title: 'Enter your estimate of your itemized deductions',
    question_type: 'short_text',
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[0-9]+(.[0-9]{1,2})?$',
      },
    ],
  },
  {
    id: 'filing_status_b',
    title: 'Select your filing status: (Line k)',
    question_type: 'multiple_choice',
    options: [
      {
        codeValue: 'single_or_married_filing_separately_or_dependent',
        shortName: 'Single, married/registered domestic partners filing separately or a dependent ($6,500)',
      },
      {
        codeValue: 'head_of_household',
        shortName: 'Head of household ($9,550)',
      },
      {
        codeValue: 'married_filing_separately_or_married_filing_jointly',
        shortName: 'Married/registered domestic partner filing jointly, married filing separately on the same return, or qualifying widow(er) with dependent child ($13,000)',
      },
    ],
  },
];

export const getWorksheetSectionBQuestions = (version) => {
  if (version === '12_13') return worksheetSectionBQuestions;

  return worksheetSectionBQuestions1017;
};

export const additionalWithholdingAmtQuestion = [
  {
    id: 'additional_amt_withheld',
    title: 'Additional amount, if any, you want withheld from each paycheck',
    question_type: 'short_text',
    is_required: false,
    rules: [
      {
        category: 'regex',
        action: 'match',
        value: '^[0-9]+(.[0-9]{1,2})?$',
      },
    ],
  },
];

export const stateOfDomicileQuestions = [
  {
    id: 'state_of_domicile',
    title: 'If yes, give the name of state of domicile',
    is_required: true,
    question_type: 'dropdown',
    options: stateOptions,
  },
];

export const exemptionQuestions = [
  {
    id: 'exempt_text',
    question_type: 'text_heading',
    content:
      'I am exempt because: last year I did not owe any DC income tax and had a right to a full refund of all DC income tax withheld from me; and this year I do not expect to owe any DC income tax and expect a full refund of all DC income tax withheld from me; and I qualify for exempt status on federal Form W-4.',
  },
  {
    id: 'student',
    title: 'If claiming exemption from withholding, are you a full-time student?',
    question_type: 'multiple_choice',
    is_required: true,
    options: [
      {
        codeValue: true,
        shortName: 'Yes',
      },
      {
        codeValue: false,
        shortName: 'No',
      },
    ],
  },
];
